import { formatCurrency, toUnitCharacter } from "@sa-nu/shared-utils/price";
import type { ReactNode } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { z } from "zod";

import { LEGAL_DOCUMENTS } from "../../../../constants";
import {
  ReservationAgreeButton,
  type ReservationAgreeButtonMessages,
} from "../../ui/buttons/reservation-agree-button";

import { DescriptionHeader } from "./description-header";

export const evChargerOptionsFormSchema = z
  .object({
    useEvCharger: z.boolean(),
    agreeEVChargerTerms: z.boolean(),
  })
  .refine((data) => {
    const { useEvCharger, agreeEVChargerTerms } = data;
    return (useEvCharger && agreeEVChargerTerms) || !useEvCharger;
  });

export type DescriptionForEVChargerMessages = {
  heading: ReactNode;
  useEvChargerCheckboxLabel: string;
  feeWithCharger: (formattedFee: string) => string;
  noAdditionalFee: string;
  legalNoteBefore: string;
  legalLinkText: string;
  legalNoteAfter: string;
  agreeButtonLabel: string;
  unitCharacter: string;
  agreeButtonMessages?: ReservationAgreeButtonMessages;
};

const JA_MESSAGES: DescriptionForEVChargerMessages = {
  heading: (
    <>
      こちらのキャビンは、
      <br />
      EV充電器が設置されています
    </>
  ),
  useEvChargerCheckboxLabel: "EV充電器を利用する",
  feeWithCharger: (formattedFee) =>
    `（滞在あたり ${formattedFee}が追加されます）`,
  noAdditionalFee: "（追加料金はかかりません）",
  legalNoteBefore: "",
  legalLinkText: "こちらの利用規則",
  legalNoteAfter: "をお読みいただき、ご同意の上でご利用ください。",
  agreeButtonLabel: "利用規則に同意します",
  unitCharacter: "円",
};

type Props = {
  skipAgreement?: boolean;
  additionalFee: number;
  messages?: DescriptionForEVChargerMessages;
};

export default function DescriptionForEVChargerFormPartial({
  skipAgreement = false,
  additionalFee,
  messages = JA_MESSAGES,
}: Props) {
  const { register, control } = useFormContext();

  const useEvCharger = useWatch({
    name: "evChargerOptions.useEvCharger",
    control,
  });

  return (
    <div className="mt-[20px] w-full space-y-[20px]">
      <DescriptionHeader iconSrc="/i/icon_evstation.svg" iconAlt="EVCharger">
        {messages.heading}
      </DescriptionHeader>
      <div className="text-xs md:text-sm">
        <label
          className="hover:cursor-pointer"
          data-testid="ev-charger-checkbox"
        >
          <input
            type="checkbox"
            {...register("evChargerOptions.useEvCharger")}
            className="mr-[9px] hover:cursor-pointer"
          />
          {messages.useEvChargerCheckboxLabel}
          {additionalFee > 0
            ? messages.feeWithCharger(
                formatCurrency(additionalFee, {
                  unitCharacter: toUnitCharacter(messages.unitCharacter),
                }),
              )
            : messages.noAdditionalFee}
        </label>
      </div>
      {useEvCharger && !skipAgreement && (
        <>
          <div className="text-xs md:text-sm">
            {messages.legalNoteBefore}
            <a
              data-analytics-id="reservation_form_clicked_ev_charger_terms"
              href={LEGAL_DOCUMENTS.evCharger.url}
              target="_blank"
              className="font-bold underline"
            >
              {messages.legalLinkText}
            </a>
            {messages.legalNoteAfter}
          </div>
          <div className="flex justify-center">
            <ReservationAgreeButton
              label={messages.agreeButtonLabel}
              required
              inputRHFArgs={register("evChargerOptions.agreeEVChargerTerms")}
              testId="ev-charger-terms-checkbox"
              messages={messages.agreeButtonMessages}
            />
          </div>
        </>
      )}
    </div>
  );
}
